/**
 * AI Feature Flag Management Page
 *
 * Admin page for managing AI feature flags per vendor.
 * Supports toggling flags, editing model configuration, and CRUD operations.
 * Uses Card/Paper patterns matching TaskManagement/BookingManagement conventions.
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Grid,
  Card,
  Button,
  Table,
  Switch,
  Badge,
  Modal,
  TextInput,
  Select,
  NumberInput,
  Loader,
  Alert,
  ThemeIcon,
  Divider,
  HtmlEditor,
} from '@/components/ui';
import { ActionIcon } from '@mantine/core';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconRefresh,
  IconAlertCircle,
  IconArrowLeft,
  IconFlag,
  IconCheck,
  IconX,
  IconBuildingStore,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAiFeatureFlagService } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import type {
  AiFeatureFlag,
  CreateAiFeatureFlagRequest,
  UpdateAiFeatureFlagRequest,
} from '@actschurch/ccms-sdk';

const FEATURE_TYPES = [
  { value: 'chat', label: 'Chat' },
  { value: 'summarization', label: 'Summarization' },
  { value: 'data_extraction', label: 'Data Extraction' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'classification', label: 'Classification' },
  { value: 'generation', label: 'Generation' },
];

const MODEL_PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google' },
  { value: 'azure_openai', label: 'Azure OpenAI' },
];

interface FlagFormValues {
  vendorID: number;
  featureType: string;
  isEnabled: boolean;
  modelId: string;
  modelProvider: string;
  maxTokens: number;
  temperature: number;
  monthlyBudgetCents: number | null;
  notes: string;
}

const DEFAULT_FORM: FlagFormValues = {
  vendorID: 1,
  featureType: '',
  isEnabled: true,
  modelId: '',
  modelProvider: 'openai',
  maxTokens: 4096,
  temperature: 0.7,
  monthlyBudgetCents: null,
  notes: '',
};

export default function FeatureFlagsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const service = useAiFeatureFlagService();

  const [modalOpened, setModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [editingFlag, setEditingFlag] = useState<AiFeatureFlag | null>(null);
  const [deletingFlag, setDeletingFlag] = useState<AiFeatureFlag | null>(null);
  const [form, setForm] = useState<FlagFormValues>(DEFAULT_FORM);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Fetch all feature flags
  const {
    data: flags = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ai-feature-flags'],
    queryFn: () => service.getAll(),
  });

  // Stat computations
  const stats = useMemo(() => {
    const enabled = flags.filter((f) => f.isEnabled).length;
    const disabled = flags.filter((f) => !f.isEnabled).length;
    const vendors = new Set(flags.map((f) => f.vendorID)).size;
    return { total: flags.length, enabled, disabled, vendors };
  }, [flags]);

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: number; isEnabled: boolean }) =>
      service.toggle(id, isEnabled),
    onSuccess: (flag, vars) => {
      queryClient.invalidateQueries({ queryKey: ['ai-feature-flags'] });
      toast.success(vars.isEnabled ? 'Switched on' : 'Switched off', flag?.featureType);
    },
    onError: (err: Error) => {
      toast.error('Could not change the switch', err.message);
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateAiFeatureFlagRequest) => service.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-feature-flags'] });
      toast.success('Feature flag created');
      closeModal();
    },
    onError: (err: Error) => {
      setSaveError(err.message);
      toast.error('Could not create the feature flag', err.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAiFeatureFlagRequest }) =>
      service.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-feature-flags'] });
      toast.success('Feature flag saved');
      closeModal();
    },
    onError: (err: Error) => {
      setSaveError(err.message);
      toast.error('Could not save the feature flag', err.message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => service.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-feature-flags'] });
      toast.success('Feature flag deleted');
      setDeleteModalOpened(false);
      setDeletingFlag(null);
    },
    onError: (err: Error) => {
      toast.error('Could not delete the feature flag', err.message);
    },
  });

  const openCreateModal = () => {
    setEditingFlag(null);
    setForm(DEFAULT_FORM);
    setModalOpened(true);
  };

  const openEditModal = (flag: AiFeatureFlag) => {
    setEditingFlag(flag);
    setForm({
      vendorID: flag.vendorID,
      featureType: flag.featureType,
      isEnabled: flag.isEnabled,
      modelId: flag.modelId || '',
      modelProvider: flag.modelProvider,
      maxTokens: flag.maxTokens,
      temperature: flag.temperature,
      monthlyBudgetCents: flag.monthlyBudgetCents ?? null,
      notes: flag.notes || '',
    });
    setModalOpened(true);
  };

  const closeModal = () => {
    setSaveError(null);
    setModalOpened(false);
    setEditingFlag(null);
    setForm(DEFAULT_FORM);
  };

  const handleSubmit = () => {
    setSaveError(null);
    const payload: CreateAiFeatureFlagRequest = {
      vendorID: form.vendorID,
      featureType: form.featureType,
      isEnabled: form.isEnabled,
      modelId: form.modelId || null,
      modelProvider: form.modelProvider,
      maxTokens: form.maxTokens,
      temperature: form.temperature,
      monthlyBudgetCents: form.monthlyBudgetCents,
      notes: form.notes || null,
    };

    if (editingFlag) {
      updateMutation.mutate({ id: editingFlag.featureFlagID, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const formatCurrency = (cents: number | null | undefined) => {
    if (cents == null) return '--';
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={() => navigate('/admin/ai')}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>AI Feature Flags</Title>
              <Text c="dimmed" size="sm">
                Enable or disable AI features and configure model settings per tenant.
              </Text>
            </div>
          </Group>
        </Group>

        {/* Stat Cards */}
        <Grid>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="blue">
                  <IconFlag size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Total Flags
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.total}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="green">
                  <IconCheck size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Enabled
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.enabled}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="red">
                  <IconX size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Disabled
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.disabled}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="violet">
                  <IconBuildingStore size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Vendors
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.vendors}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Error */}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error" withCloseButton>
            {(error as Error).message || 'Failed to load feature flags'}
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <Group justify="center" p="xl">
            <Loader size="lg" />
          </Group>
        )}

        {/* Table in Card */}
        {!isLoading && (
          <Card shadow="sm" padding={0} radius="md" withBorder>
            {/* Card Header */}
            <Group justify="space-between" p="lg" pb="sm">
              <Group gap="xs">
                <IconFlag size={20} />
                <Title order={4}>Feature Flags</Title>
              </Group>
              <Group gap="xs">
                <Button
                  variant="subtle"
                  size="sm"
                  leftSection={<IconRefresh size={16} />}
                  onClick={() => refetch()}
                  loading={isLoading}
                >
                  Refresh
                </Button>
                <Button size="sm" leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
                  New Flag
                </Button>
              </Group>
            </Group>
            <Divider />

            <Table tableVariant="striped">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Feature Type</Table.Th>
                  <Table.Th>Provider</Table.Th>
                  <Table.Th>Enabled</Table.Th>
                  <Table.Th>Model</Table.Th>
                  <Table.Th>Max Tokens</Table.Th>
                  <Table.Th>Temperature</Table.Th>
                  <Table.Th>Budget</Table.Th>
                  <Table.Th>Usage</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {flags.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={9}>
                      <Text ta="center" c="dimmed" py="lg">
                        No feature flags found. Click "New Flag" to create one.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {flags.map((flag) => (
                  <Table.Tr key={flag.featureFlagID}>
                    <Table.Td>
                      <Badge variant="light" color="blue">
                        {flag.featureType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="dot" color="gray">
                        {flag.modelProvider}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={flag.isEnabled}
                        onChange={() =>
                          toggleMutation.mutate({
                            id: flag.featureFlagID,
                            isEnabled: !flag.isEnabled,
                          })
                        }
                        disabled={toggleMutation.isPending}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{flag.modelId || '--'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{flag.maxTokens.toLocaleString()}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{flag.temperature}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatCurrency(flag.monthlyBudgetCents)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatCurrency(flag.currentMonthUsage)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => openEditModal(flag)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => {
                            setDeletingFlag(flag);
                            setDeleteModalOpened(true);
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </Stack>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={editingFlag ? 'Edit Feature Flag' : 'Create Feature Flag'}
        size="lg"
      >
        <Stack gap="md">
          <NumberInput
            label="Vendor ID"
            value={form.vendorID}
            onChange={(val) => setForm((f) => ({ ...f, vendorID: typeof val === 'number' ? val : f.vendorID }))}
            min={1}
            required
          />
          <Select
            label="Feature Type"
            placeholder="Select feature type"
            data={FEATURE_TYPES}
            value={form.featureType}
            onChange={(val) => setForm((f) => ({ ...f, featureType: val || '' }))}
            required
            searchable
            allowDeselect={false}
          />
          <Select
            label="Model Provider"
            placeholder="Select provider"
            data={MODEL_PROVIDERS}
            value={form.modelProvider}
            onChange={(val) => setForm((f) => ({ ...f, modelProvider: val || 'openai' }))}
            required
            allowDeselect={false}
          />
          <TextInput
            label="Model ID"
            placeholder="e.g. gpt-4o, claude-sonnet-4-20250514"
            value={form.modelId}
            onChange={(e) => setForm((f) => ({ ...f, modelId: e.currentTarget.value }))}
          />
          <Group grow>
            <NumberInput
              label="Max Tokens"
              value={form.maxTokens}
              onChange={(val) => setForm((f) => ({ ...f, maxTokens: Number(val) || 4096 }))}
              min={1}
              max={200000}
            />
            <NumberInput
              label="Temperature"
              value={form.temperature}
              onChange={(val) => setForm((f) => ({ ...f, temperature: Number(val) || 0 }))}
              min={0}
              max={2}
              step={0.1}
              decimalScale={2}
            />
          </Group>
          <NumberInput
            label="Monthly Budget (cents)"
            description="Leave empty for unlimited"
            value={form.monthlyBudgetCents ?? ''}
            onChange={(val) =>
              setForm((f) => ({
                ...f,
                monthlyBudgetCents: val === '' ? null : Number(val),
              }))
            }
            min={0}
          />
          <Switch
            label="Enabled"
            checked={form.isEnabled}
            onChange={(e) => setForm((f) => ({ ...f, isEnabled: e.currentTarget.checked }))}
          />
          <HtmlEditor
            label="Notes"
            placeholder="Optional notes about this feature flag"
            value={form.notes}
            onChange={(_html, text) => setForm((f) => ({ ...f, notes: text }))}
            minHeight={80}
          />
          {saveError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Not saved">
              {saveError}
            </Alert>
          )}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
              disabled={!form.featureType || !form.modelProvider}
            >
              {editingFlag ? 'Update' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          setDeleteModalOpened(false);
          setDeletingFlag(null);
        }}
        title="Delete Feature Flag"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the feature flag{' '}
            <Text span fw={600}>
              {deletingFlag?.featureType}
            </Text>
            ? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteModalOpened(false);
                setDeletingFlag(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => {
                if (deletingFlag) {
                  deleteMutation.mutate(deletingFlag.featureFlagID);
                }
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
